<template>
  <div class="sales-history">
    <div class="page-header">
      <h1>📋 Sales History</h1>
      <div class="header-actions">
        <button @click="refreshData" :disabled="loading" class="btn btn-primary">
          <span v-if="loading" class="spinner-small"></span>
          <span v-else>🔄</span>
          Refresh
        </button>
        <router-link to="/dashboard" class="btn btn-secondary">
          📊 Dashboard
        </router-link>
      </div>
    </div>

    <!-- Filters and Search -->
    <div class="filters-section">
      <div class="search-bar">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by product name, barcode, or cashier..."
          class="search-input"
          @input="handleSearch"
        />
        <button v-if="searchQuery" @click="clearSearch" class="btn btn-small btn-secondary">
          ✕ Clear
        </button>
      </div>

      <div class="filters">
        <div class="filter-group">
          <label for="statusFilter">Status:</label>
          <select id="statusFilter" v-model="filters.status" @change="applyFilters" class="filter-select">
            <option value="">All</option>
            <option value="COMPLETED">Completed</option>
            <option value="VOIDED">Voided</option>
          </select>
        </div>

        <div class="filter-group">
          <label for="cashierFilter">Cashier:</label>
          <select id="cashierFilter" v-model="filters.cashier" @change="applyFilters" class="filter-select">
            <option value="">All Cashiers</option>
            <option v-for="cashier in uniqueCashiers" :key="cashier" :value="cashier">
              {{ cashier }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label for="dateRange">Date Range:</label>
          <select id="dateRange" v-model="filters.dateRange" @change="applyDateFilter" class="filter-select">
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        <div v-if="filters.dateRange === 'custom'" class="date-inputs">
          <input
            v-model="customDateRange.start"
            type="date"
            class="date-input"
            @change="applyFilters"
          />
          <span>to</span>
          <input
            v-model="customDateRange.end"
            type="date"
            class="date-input"
            @change="applyFilters"
          />
        </div>

        <button @click="clearAllFilters" class="btn btn-small btn-secondary">
          Clear Filters
        </button>
      </div>
    </div>

    <!-- Sales Summary -->
    <div v-if="salesData" class="summary-bar">
      <div class="summary-item">
        <span class="label">Total Sales:</span>
        <span class="value">{{ salesData.pagination.totalCount }}</span>
      </div>
      <div class="summary-item">
        <span class="label">Total Revenue:</span>
        <span class="value">${{ totalRevenue.toFixed(2) }}</span>
      </div>
      <div class="summary-item">
        <span class="label">Showing:</span>
        <span class="value">{{ salesData.sales.length }} of {{ salesData.pagination.totalCount }}</span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading sales history...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-message">
        <h3>⚠️ Error Loading Sales</h3>
        <p>{{ error }}</p>
        <button @click="refreshData" class="btn btn-primary">Try Again</button>
      </div>
    </div>

    <!-- Sales Table -->
    <div v-else-if="salesData && salesData.sales.length > 0" class="table-container">
      <div class="table-wrapper">
        <table class="sales-table">
          <thead>
            <tr>
              <th @click="sortBy('createdAt')" class="sortable">
                Date & Time
                <span v-if="sortField === 'createdAt'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sortBy('productName')" class="sortable">
                Product
                <span v-if="sortField === 'productName'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sortBy('quantitySold')" class="sortable">
                Quantity
                <span v-if="sortField === 'quantitySold'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sortBy('priceAtSale')" class="sortable">
                Unit Price
                <span v-if="sortField === 'priceAtSale'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sortBy('totalAmount')" class="sortable">
                Total
                <span v-if="sortField === 'totalAmount'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sortBy('cashier')" class="sortable">
                Cashier
                <span v-if="sortField === 'cashier'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sortBy('status')" class="sortable">
                Status
                <span v-if="sortField === 'status'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th v-if="authStore.user?.role === 'OWNER'" class="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="sale in salesData.sales"
              :key="sale.id"
              :class="{ 'voided-row': sale.status === 'VOIDED' }"
            >
              <td class="date-cell">
                <div class="date-info">
                  <span class="date">{{ formatDate(sale.createdAt) }}</span>
                  <span class="time">{{ formatTime(sale.createdAt) }}</span>
                </div>
              </td>
              <td class="product-cell">
                <div class="product-info">
                  <span class="product-name">{{ sale.productName }}</span>
                  <span class="barcode">{{ sale.barcode }}</span>
                </div>
              </td>
              <td class="quantity-cell">
                <span class="quantity">{{ sale.quantitySold }}</span>
              </td>
              <td class="price-cell">
                <span class="price">${{ sale.priceAtSale.toFixed(2) }}</span>
              </td>
              <td class="total-cell">
                <span class="total">${{ sale.totalAmount.toFixed(2) }}</span>
              </td>
              <td class="cashier-cell">
                <div class="cashier-info">
                  <span class="cashier-name">{{ sale.cashier }}</span>
                  <span class="cashier-email">{{ sale.cashierEmail }}</span>
                </div>
              </td>
              <td class="status-cell">
                <span :class="['status-badge', sale.status.toLowerCase()]">
                  {{ sale.status }}
                </span>
              </td>
              <td v-if="authStore.user?.role === 'OWNER'" class="actions-cell">
                <button
                  v-if="sale.status === 'COMPLETED'"
                  @click="voidSale(sale)"
                  :disabled="voidingIds.includes(sale.id)"
                  class="btn btn-small btn-danger"
                  title="Void this sale"
                >
                  <span v-if="voidingIds.includes(sale.id)" class="spinner-small"></span>
                  <span v-else>❌</span>
                  Void
                </button>
                <span v-else class="action-disabled">Voided</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="salesData && salesData.sales.length === 0" class="empty-state">
      <div class="empty-icon">📋</div>
      <h3>No Sales Found</h3>
      <p v-if="hasActiveFilters">No sales match your current filters.</p>
      <p v-else>No sales have been recorded yet.</p>
      <div class="empty-actions">
        <button v-if="hasActiveFilters" @click="clearAllFilters" class="btn btn-primary">
          Clear Filters
        </button>
        <router-link v-else to="/scan" class="btn btn-primary">
          Start Selling
        </router-link>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="salesData && salesData.pagination.totalPages > 1" class="pagination">
      <button
        @click="goToPage(1)"
        :disabled="currentPage === 1"
        class="btn btn-small btn-secondary"
      >
        First
      </button>
      <button
        @click="goToPage(currentPage - 1)"
        :disabled="!salesData.pagination.hasPreviousPage"
        class="btn btn-small btn-secondary"
      >
        Previous
      </button>
      
      <div class="page-info">
        <span>Page {{ currentPage }} of {{ salesData.pagination.totalPages }}</span>
        <select v-model="pageSize" @change="changePageSize" class="page-size-select">
          <option value="10">10 per page</option>
          <option value="25">25 per page</option>
          <option value="50">50 per page</option>
          <option value="100">100 per page</option>
        </select>
      </div>
      
      <button
        @click="goToPage(currentPage + 1)"
        :disabled="!salesData.pagination.hasNextPage"
        class="btn btn-small btn-secondary"
      >
        Next
      </button>
      <button
        @click="goToPage(salesData.pagination.totalPages)"
        :disabled="currentPage === salesData.pagination.totalPages"
        class="btn btn-small btn-secondary"
      >
        Last
      </button>
    </div>

    <!-- Void Confirmation Modal -->
    <div v-if="showVoidModal" class="modal-overlay" @click="cancelVoid">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>⚠️ Void Sale</h3>
          <button @click="cancelVoid" class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to void this sale?</p>
          <div v-if="saleToVoid" class="sale-details">
            <p><strong>Product:</strong> {{ saleToVoid.productName }}</p>
            <p><strong>Quantity:</strong> {{ saleToVoid.quantitySold }}</p>
            <p><strong>Total:</strong> ${{ saleToVoid.totalAmount.toFixed(2) }}</p>
            <p><strong>Date:</strong> {{ formatDateTime(saleToVoid.createdAt) }}</p>
          </div>
          <p class="warning">This action will restore the product stock and cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button @click="cancelVoid" class="btn btn-secondary">Cancel</button>
          <button @click="confirmVoid" :disabled="voidingIds.length > 0" class="btn btn-danger">
            <span v-if="voidingIds.length > 0" class="spinner-small"></span>
            Void Sale
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAuthStore } from '../stores/auth'
import api from '../services/api'

const authStore = useAuthStore()

// State
const salesData = ref(null)
const loading = ref(false)
const error = ref(null)
const searchQuery = ref('')
const currentPage = ref(1)
const pageSize = ref(25)
const sortField = ref('createdAt')
const sortDirection = ref('desc')
const voidingIds = ref([])
const showVoidModal = ref(false)
const saleToVoid = ref(null)

// Filters
const filters = ref({
  status: '',
  cashier: '',
  dateRange: ''
})

const customDateRange = ref({
  start: '',
  end: ''
})

// Computed properties
const totalRevenue = computed(() => {
  if (!salesData.value) return 0
  return salesData.value.sales.reduce((sum, sale) => {
    return sale.status === 'COMPLETED' ? sum + sale.totalAmount : sum
  }, 0)
})

const uniqueCashiers = computed(() => {
  if (!salesData.value) return []
  const cashiers = new Set()
  salesData.value.sales.forEach(sale => {
    if (sale.cashier) cashiers.add(sale.cashier)
  })
  return Array.from(cashiers).sort()
})

const hasActiveFilters = computed(() => {
  return !!(searchQuery.value || filters.value.status || filters.value.cashier || filters.value.dateRange)
})

// Methods
const fetchSalesData = async () => {
  loading.value = true
  error.value = null
  
  try {
    const params = new URLSearchParams({
      page: currentPage.value.toString(),
      limit: pageSize.value.toString()
    })
    
    // Add filters
    if (filters.value.status) params.append('status', filters.value.status)
    if (filters.value.cashier) params.append('cashier', filters.value.cashier)
    if (searchQuery.value) params.append('search', searchQuery.value)
    
    // Add date filters
    if (filters.value.dateRange === 'custom' && customDateRange.value.start && customDateRange.value.end) {
      params.append('startDate', customDateRange.value.start)
      params.append('endDate', customDateRange.value.end)
    } else if (filters.value.dateRange) {
      const dateRange = calculateDateRange(filters.value.dateRange)
      if (dateRange.start) params.append('startDate', dateRange.start)
      if (dateRange.end) params.append('endDate', dateRange.end)
    }
    
    // Add sorting
    params.append('sortBy', sortField.value)
    params.append('sortDirection', sortDirection.value)
    
    const response = await api.get(`/sales?${params.toString()}`)
    salesData.value = response.data
    
  } catch (err) {
    console.error('Sales fetch error:', err)
    error.value = err.response?.data?.error || 'Failed to load sales history'
  } finally {
    loading.value = false
  }
}

const refreshData = () => {
  fetchSalesData()
}

// Search functionality
let searchTimeout = null
const handleSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    fetchSalesData()
  }, 500) // Debounce search
}

const clearSearch = () => {
  searchQuery.value = ''
  currentPage.value = 1
  fetchSalesData()
}

// Filter functionality
const applyFilters = () => {
  currentPage.value = 1
  fetchSalesData()
}

const applyDateFilter = () => {
  if (filters.value.dateRange !== 'custom') {
    customDateRange.value.start = ''
    customDateRange.value.end = ''
  }
  applyFilters()
}

const calculateDateRange = (range) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  
  switch (range) {
    case 'today': {
      return {
        start: today.toISOString().split('T')[0],
        end: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }
    }
    case 'yesterday': {
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
      return {
        start: yesterday.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      }
    }
    case 'week': {
      const weekStart = new Date(today)
      weekStart.setDate(today.getDate() - today.getDay())
      return {
        start: weekStart.toISOString().split('T')[0],
        end: null
      }
    }
    case 'month': {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
      return {
        start: monthStart.toISOString().split('T')[0],
        end: null
      }
    }
    default:
      return { start: null, end: null }
  }
}

const clearAllFilters = () => {
  searchQuery.value = ''
  filters.value = {
    status: '',
    cashier: '',
    dateRange: ''
  }
  customDateRange.value = {
    start: '',
    end: ''
  }
  currentPage.value = 1
  fetchSalesData()
}

// Sorting functionality
const sortBy = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'desc'
  }
  fetchSalesData()
}

// Pagination
const goToPage = (page) => {
  currentPage.value = page
  fetchSalesData()
}

const changePageSize = () => {
  currentPage.value = 1
  fetchSalesData()
}

// Void functionality
const voidSale = (sale) => {
  saleToVoid.value = sale
  showVoidModal.value = true
}

const confirmVoid = async () => {
  if (!saleToVoid.value) return
  
  const saleId = saleToVoid.value.id
  voidingIds.value.push(saleId)
  
  try {
    await api.put(`/sales/${saleId}/void`)
    
    // Refresh data to show updated status
    await fetchSalesData()
    
    showVoidModal.value = false
    saleToVoid.value = null
    
  } catch (err) {
    console.error('Void sale error:', err)
    error.value = err.response?.data?.error || 'Failed to void sale'
  } finally {
    voidingIds.value = voidingIds.value.filter(id => id !== saleId)
  }
}

const cancelVoid = () => {
  showVoidModal.value = false
  saleToVoid.value = null
}

// Utility functions
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString()
}

const formatTime = (dateString) => {
  return new Date(dateString).toLocaleTimeString()
}

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString()
}

// Watchers
watch([searchQuery, filters], () => {
  if (currentPage.value !== 1) {
    currentPage.value = 1
  }
}, { deep: true })

// Lifecycle
onMounted(() => {
  fetchSalesData()
})
</script>

<style scoped>
.sales-history {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
}

.page-header h1 {
  color: #2c3e50;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Filters Section */
.filters-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.search-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.search-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 1em;
}

.search-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-weight: 500;
  color: #495057;
  font-size: 0.9em;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  min-width: 120px;
}

.date-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

/* Summary Bar */
.summary-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  padding: 15px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.summary-item {
  display: flex;
  flex-direction: column;
  text-align: center;
}

.summary-item .label {
  font-size: 0.9em;
  color: #6c757d;
  margin-bottom: 4px;
}

.summary-item .value {
  font-size: 1.1em;
  font-weight: 600;
  color: #2c3e50;
}

/* Loading and Error States */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  text-align: center;
  color: #dc3545;
}

/* Table */
.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.table-wrapper {
  overflow-x: auto;
}

.sales-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.sales-table th,
.sales-table td {
  padding: 15px 12px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.sales-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sales-table th.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
}

.sales-table th.sortable:hover {
  background: #e9ecef;
}

.sort-indicator {
  margin-left: 5px;
  font-size: 0.8em;
}

.sales-table tr:hover {
  background: #f8f9fa;
}

.voided-row {
  opacity: 0.6;
  background: #fff5f5 !important;
}

/* Table Cell Styles */
.date-info {
  display: flex;
  flex-direction: column;
}

.date {
  font-weight: 500;
  color: #2c3e50;
}

.time {
  font-size: 0.9em;
  color: #6c757d;
}

.product-info {
  display: flex;
  flex-direction: column;
}

.product-name {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.barcode {
  font-size: 0.8em;
  color: #6c757d;
  font-family: monospace;
}

.quantity {
  font-weight: 600;
  color: #007bff;
}

.price,
.total {
  font-weight: 600;
  color: #28a745;
}

.cashier-info {
  display: flex;
  flex-direction: column;
}

.cashier-name {
  font-weight: 500;
  color: #2c3e50;
}

.cashier-email {
  font-size: 0.8em;
  color: #6c757d;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.completed {
  background: #d4edda;
  color: #155724;
}

.status-badge.voided {
  background: #f8d7da;
  color: #721c24;
}

.actions-cell {
  text-align: center;
}

.action-disabled {
  color: #6c757d;
  font-style: italic;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #6c757d;
}

.empty-icon {
  font-size: 5em;
  margin-bottom: 20px;
  opacity: 0.6;
}

.empty-state h3 {
  color: #495057;
  margin-bottom: 10px;
}

.empty-actions {
  margin-top: 20px;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 20px;
}

.page-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  margin: 0 20px;
}

.page-size-select {
  padding: 4px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.8em;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  color: #dc3545;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 20px;
}

.sale-details {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin: 15px 0;
}

.sale-details p {
  margin: 5px 0;
}

.warning {
  color: #dc3545;
  font-weight: 500;
  margin-top: 15px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e9ecef;
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
}

.btn-small {
  padding: 6px 12px;
  font-size: 0.8em;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .summary-bar {
    flex-direction: column;
    gap: 15px;
  }
  
  .sales-table {
    font-size: 0.9em;
  }
  
  .sales-table th,
  .sales-table td {
    padding: 10px 8px;
  }
  
  .pagination {
    flex-wrap: wrap;
    gap: 5px;
  }
  
  .page-info {
    order: -1;
    margin: 0 0 10px 0;
  }
}
</style> 